<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'User Profile'">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'UserAccount' })"
        />
      </template>
    </MainHeader>

    <div class="view up-view">
      <!-- Tab switcher -->
      <div class="et-tabs">
        <button
          class="et-tab"
          :class="{ active: !editTransactionSliderSelected }"
          @click="editTransactionSliderSelected = false"
        >
          Info
        </button>
        <button
          class="et-tab"
          :class="{ active: editTransactionSliderSelected }"
          @click="editTransactionSliderSelected = true"
        >
          Edit
        </button>
      </div>

      <!-- ── INFO TAB ── -->
      <div v-if="!editTransactionSliderSelected">
        <!-- Avatar hero -->
        <div class="up-hero">
          <div class="up-avatar">
            <font-awesome-icon
              :icon="['fas', 'circle-user']"
              class="up-avatar-icon"
            />
          </div>
          <h2 class="up-name">{{ user.firstName }} {{ user.lastName }}</h2>
          <p class="up-preferred" v-if="user.preferredName">
            {{ user.preferredName }}
          </p>
        </div>

        <div class="wrapper-card up-info-card">
          <div class="ud-section">
            <span class="section-label">Personal</span>
            <div class="et-info-row">
              <span class="et-info-label">Full Name</span>
              <span class="et-info-value"
                >{{ user.firstName }} {{ user.middleName }}
                {{ user.lastName }}</span
              >
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Preferred Name</span>
              <span class="et-info-value">{{
                user.preferredName || 'N/A'
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Date of Birth</span>
              <span class="et-info-value">{{
                user.dateOfBirth ? formatDateOfBirth(user.dateOfBirth) : 'N/A'
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Gender</span>
              <span class="et-info-value">{{ user.gender || 'N/A' }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Occupation</span>
              <span class="et-info-value">{{ user.occupation || 'N/A' }}</span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Contact</span>
            <div class="et-info-row">
              <span class="et-info-label">Email</span>
              <span class="et-info-value">{{ user.email }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Phone</span>
              <span class="et-info-value">{{ user.phoneNumber || 'N/A' }}</span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Address</span>
            <div class="et-info-row">
              <span class="et-info-label">Street</span>
              <span class="et-info-value">{{ user.address || 'N/A' }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">City</span>
              <span class="et-info-value">{{ user.city || 'N/A' }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">State</span>
              <span class="et-info-value">{{ user.state || 'N/A' }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Zipcode</span>
              <span class="et-info-value">{{ user.zipCode || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <div v-if="store.isUserOver18" class="mt-1">
          <button
            v-if="!user.premiumSubscription"
            class="gradient-button w-100"
            @click="subscribePremium"
          >
            ✦ Upgrade to Premium
          </button>
          <button
            v-else
            class="outline-button w-100"
            @click="openCancelSubscriptionModal"
          >
            Cancel Premium Subscription
          </button>
        </div>

        <button class="delete-btn w-100 mt-1" @click="openDeleteAccountModal">
          Delete Account
        </button>
      </div>

      <!-- ── EDIT TAB ── -->
      <div v-else>
        <form @submit.prevent="UpdateUserProfile">
          <fieldset>
            <div class="ud-section">
              <span class="section-label">Personal</span>

              <div class="ud-field">
                <label class="ud-label" for="firstname"
                  >First Name <span class="ud-asterisk">*</span></label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="firstName"
                  required
                  id="firstname"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="displayname"
                  >Preferred Display Name</label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="preferredName"
                  id="displayname"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="middlename">Middle Name</label>
                <input
                  type="text"
                  class="ud-input"
                  v-model="middleName"
                  id="middlename"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="lastname"
                  >Last Name <span class="ud-asterisk">*</span></label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="lastName"
                  required
                  id="lastname"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="dob"
                  >Date of Birth <span class="ud-asterisk">*</span></label
                >
                <input
                  id="dob"
                  type="date"
                  class="ud-input date-picker"
                  v-model="dateOfBirthForEdit"
                  required
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="gender">Gender</label>
                <select
                  id="gender"
                  v-model="gender"
                  class="ud-input ud-select form-select"
                >
                  <option value="" disabled>Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="job">Occupation</label>
                <input
                  id="job"
                  type="text"
                  class="ud-input"
                  v-model="occupationInput"
                  placeholder="Start typing your occupation..."
                  @input="filterOccupations"
                  @blur="handleOccupationBlur"
                />
                <div
                  v-if="filteredOccupations.length > 0"
                  class="autocomplete-list wrapper-card"
                >
                  <div
                    v-for="(item, index) in filteredOccupations"
                    :key="index"
                    class="autocomplete-item"
                    @click="selectOccupation(item)"
                  >
                    {{ item }}
                  </div>
                </div>
              </div>
            </div>

            <div class="ud-section">
              <span class="section-label">Contact</span>

              <div class="ud-field">
                <label class="ud-label" for="email"
                  >Email Address <span class="ud-asterisk">*</span></label
                >
                <input
                  id="email"
                  type="email"
                  class="ud-input"
                  v-model="email"
                  autocomplete="email"
                  required
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  class="ud-input"
                  v-model="phoneNumber"
                  autocomplete="tel"
                  pattern="(\([0-9]{3}\)|[0-9]{3})-[0-9]{3}-[0-9]{4}"
                  title="Enter a valid US phone number (e.g., 555-555-5555)"
                  placeholder="555-555-5555"
                  @input="formatPhoneNumber"
                  maxlength="12"
                />
              </div>
            </div>

            <div class="ud-section">
              <span class="section-label">Address</span>

              <div class="ud-field">
                <label class="ud-label" for="address">Street Address</label>
                <input
                  type="text"
                  class="ud-input"
                  v-model="address"
                  id="address"
                  autocomplete="street-address"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="city">City</label>
                <input
                  type="text"
                  class="ud-input"
                  v-model="city"
                  id="city"
                  autocomplete="address-level2"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="state">State</label>
                <input
                  type="text"
                  class="ud-input"
                  v-model="state"
                  id="state"
                  autocomplete="address-level1"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label" for="zipcode">Zipcode</label>
                <input
                  id="zipcode"
                  type="text"
                  class="ud-input"
                  autocomplete="postal-code"
                  v-model="zipCode"
                />
              </div>
            </div>

            <button
              role="button"
              type="submit"
              aria-label="submit update user profile"
              class="ud-submit"
            >
              Save Profile
            </button>
          </fieldset>
        </form>
      </div>
    </div>
  </div>

  <Modal ref="modalRef">
    <template #header>
      <h2>Cancel Premium</h2>
    </template>
    <template #body>
      <p>
        You are about to cancel your Premium subscription. You will lose access
        to all linked bank accounts and their transactions.
      </p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button @click="closeModal" class="teal-outline-button">
          Keep Premium
        </button>
        <button @click="cancelSubscriptionConfirmation" class="teal-button">
          Cancel Subscription
        </button>
      </div>
    </template>
  </Modal>

  <!-- Delete account modal -->
  <Modal ref="deleteModalRef">
    <template #header>
      <h2>Delete Account</h2>
    </template>
    <template #body>
      <p>
        By deleting your account, all your budgets and account information will
        be permanently removed. Any active Premium subscription will be
        cancelled automatically.
      </p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button @click="closeDeleteAccountModal" class="teal-outline-button">
          Keep My Account
        </button>
        <button @click="deleteAccount" class="teal-button">
          Delete Account
        </button>
      </div>
    </template>
  </Modal>

  <Modal ref="cancelErrorModalRef">
    <template #header>
      <h2>Unable to Cancel Subscription</h2>
    </template>
    <template #body>
      <p v-if="cancelError.message === 'apple'">
        It looks like your subscription was purchased through Apple. To cancel,
        please go to <strong>Settings → Apple ID → Subscriptions</strong> on
        your iPhone and cancel Lucky Friday there.
      </p>
      <p v-else>
        It looks like your subscription was purchased on the web. To cancel,
        please log in at <strong>luckyfriday.app</strong> and manage your
        subscription from your account settings.
      </p>
      <p style="margin-top: 0.75rem">
        If you need further assistance, our team is happy to help.
        <strong>support@luckyfridayapp.com</strong>
      </p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button @click="cancelErrorModalRef?.closeModal()" class="teal-button">
          Got it
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { useMyStore } from '../../store';
import { useRouter } from 'vue-router';
import { onMounted, ref, watch } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { Occupation_List } from '../../utils/occupationList';
import { Capacitor } from '@capacitor/core';
import MainHeader from '../../components/View-Header.vue';
import { validEmail } from '../../utils/formValidation/validEmail.js';
import { validZipcode } from '../../utils/formValidation/validZipcode.js';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import Modal from '../../components/Main-Modal.vue';
import {
  RevenueCatUI,
  PAYWALL_RESULT,
} from '@revenuecat/purchases-capacitor-ui';
import { Purchases } from '@revenuecat/purchases-capacitor';

export default {
  components: { MainHeader, SkeletonLoader, Modal },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const { logout } = useAuth0();

    let editTransactionSliderSelected = ref(false);
    const user = ref(store.user);
    const firstName = ref(user.value.firstName);
    const middleName = ref(user.value.middleName);
    const lastName = ref(user.value.lastName);
    const preferredName = ref(user.value.preferredName);
    const dateOfBirth = ref(user.value.dateOfBirth);
    const gender = ref(user.value.gender);
    const email = ref(user.value.email);
    const address = ref(user.value.address);
    const city = ref(user.value.city);
    const state = ref(user.value.state);
    const zipCode = ref(user.value.zipCode);
    const phoneNumber = ref(user.value.phoneNumber);
    const dateOfBirthForEdit = ref(user.value.dateOfBirth);
    const premiumSubscription = ref(user.value.premiumSubscription);
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const occupations = Occupation_List;
    const occupationInput = ref(
      user.value.occupation ? user.value.occupation : ''
    );
    const filteredOccupations = ref([]);
    const modalRef = ref(null);
    const deleteModalRef = ref(null);
    const isIosApp = ref(false);
    const cancelError = ref({ show: false, message: '' });
    const cancelErrorModalRef = ref(null);

    const filterOccupations = () => {
      if (!occupationInput.value) {
        filteredOccupations.value = [];
        return;
      }
      filteredOccupations.value = occupations
        .filter((occ) =>
          occ.toLowerCase().includes(occupationInput.value.toLowerCase())
        )
        .slice(0, 10);
    };

    const selectOccupation = (item) => {
      occupationInput.value = item;
      filteredOccupations.value = [];
    };

    const handleOccupationBlur = () => {
      setTimeout(() => {
        filteredOccupations.value = [];
      }, 200);
    };

    const formatPhoneNumber = (event) => {
      let digits = event.target.value.replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) {
        phoneNumber.value = digits;
      } else if (digits.length <= 6) {
        phoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        phoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    };

    const validPhoneNumber = (number) => number.length === 10;

    const UpdateUserProfile = () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;
      try {
        let userData = {
          FirstName: firstName.value,
          MiddleName: middleName.value,
          LastName: lastName.value,
          PreferredName: preferredName.value
            ? preferredName.value
            : firstName.value,
          DateOfBirth: dateOfBirthForEdit.value
            ? new Date(dateOfBirthForEdit.value + 'T00:00:00').toISOString()
            : null,
          Gender: gender.value,
          Occupation: occupationInput.value,
          Email: email.value,
          Address: address.value,
          City: city.value,
          State: state.value,
          ZipCode: zipCode.value,
          PhoneNumber: phoneNumber.value,
          DateCreated: user.value.DateCreated,
        };
        store.saveUserAccount(userData).then((response) => {
          if (response.status === 200) {
            store.getUserAccountData().then(() => {
              user.value = store.user;
              user.value.dateOfBirth = formatDateOfBirth(
                user.value.dateOfBirth
              );
              editTransactionSliderSelected.value = false;
              loading.value = false;
            });
          }
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred: ', error);
      }
    };

    const formatDateOfBirth = (dateOfBirth) => {
      const date = new Date(dateOfBirth);
      const year = date.getUTCFullYear();
      const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
      const day = ('0' + date.getUTCDate()).slice(-2);
      return `${month}/${day}/${year}`;
    };

    const deleteAccount = async () => {
      loadingText.value = 'Deleting Account... ';
      loading.value = true;
      const response = await store.deleteUserAccount();
      if (response.status === 200) {
        await store.fullLogout(
          logout,
          router,
          Capacitor.isNativePlatform(),
          import.meta.env.VITE_BUNDLE_ID,
          import.meta.env.VITE_AUTH0_DOMAIN
        );
      }
      closeDeleteAccountModal();
    };

    //kk check delete account on ios and web after pushing up!
    // const deleteAccount = () => {
    //   loadingText.value = 'Deleting Account...';
    //   loading.value = true;
    //   store.deleteUserAccount().then((response) => {
    //     if (response.status === 200) {
    //       loading.value = false;
    //       logout({ logoutParams: { returnTo: window.location.origin } });
    //     }
    //   });
    //   closeDeleteAccountModal();
    // };

    const cancelSubscription = async () => {
      if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
        try {
          await RevenueCatUI.presentCustomerCenter();
          const { customerInfo } = await Purchases.getCustomerInfo();
          const entitlement =
            customerInfo.entitlements.active['Lucky Friday Pro'];
        } catch (e) {
          console.error('presentCustomerCenter error: ', e.message);
          cancelError.value = {
            show: true,
            message: 'apple',
          };
          cancelErrorModalRef.value?.openModal();
        }
      } else {
        loadingText.value = 'Canceling premium subscription to Lucky Friday';
        try {
          const response = await store.cancelSubscription();
          if (response.status === 200) {
            await store.getUserAccountData();
          } else {
            cancelError.value = { show: true, message: 'web' };
            cancelErrorModalRef.value?.openModal();
          }
        } catch (e) {
          console.error('cancelSubscription error: ', e.message);
          cancelError.value = { show: true, message: 'web' };
          cancelErrorModalRef.value?.openModal();
        } finally {
          loading.value = false;
        }
      }
    };

    //redoing this method so that a modal displays for users trying to cancel and encountering an error.
    // const cancelSubscription = async () => {
    //   if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    //     try {
    //       await RevenueCatUI.presentCustomerCenter();

    //       const { customerInfo } = await Purchases.getCustomerInfo();
    //       const entitlement = customerInfo.entitlements.active['Lucky Friday Pro'];

    //     } catch (e) {
    //       console.error('presentCustomerCenter error: ', e.message);
    //     }
    //   } else {
    //     loadingText.value = 'Canceling premium subscription to Lucky Friday ';
    //     loading.value = true;
    //     store.cancelSubscription().then((response) => {
    //       if (response.status === 200) {
    //         store.getUserAccountData().then(() => {
    //           loading.value = false;
    //         });
    //       }
    //     });
    //   }
    // };

    //commenting this out so that I can test the expiration of subscriptions.

    // const cancelSubscription = async () => {
    //   if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    //     try {
    //       await RevenueCatUI.presentCustomerCenter();

    //       const { customerInfo } = await Purchases.getCustomerInfo();

    //       alert('customerInfo: ' + JSON.stringify(customerInfo.entitlements));

    //       const entitlement = customerInfo.entitlements.active['Lucky Friday Pro'];

    //       alert('entitlement: ' + JSON.stringify(entitlement));
    //       alert('willRenew: ' + (entitlement ? entitlement.willRenew : 'no entitlement'));

    //       const hasCancelled = entitlement && entitlement.willRenew === false;

    //       if (hasCancelled) {
    //         try {
    //           alert('About to call backend cancel');
    //           const response = await store.cancelIapSubscription();
    //           alert('Backend response status: ' + response.status);
    //           alert('Backend response data: ' + JSON.stringify(response.data));

    //           if (response.status === 200) {
    //             await store.getUserAccountData();
    //             alert('User premiumSubscription after refresh: ' + store.user.premiumSubscription);
    //           } else {
    //             alert('Backend returned non-200 status');
    //           }
    //         } catch (e) {
    //           alert('Backend call FAILED:' + e.message);
    //         }
    //       }
    //       } catch (e) {
    //         alert('presentCustomerCenter error: ' + e.message);
    //       }
    //   } else {
    //     loadingText.value = 'Canceling premium subscription to Lucky Friday ';
    //     loading.value = true;
    //     store.cancelSubscription().then((response) => {
    //       if (response.status === 200) {
    //         store.getUserAccountData().then(() => {
    //           loading.value = false;
    //         });
    //       }
    //     });
    //   }
    // };

    // const cancelSubscription = async () => {
    //   if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    //     try {
    //       await RevenueCatUI.presentCustomerCenter();

    //       const { customerInfo } = await Purchases.getCustomerInfo();
    //       const entitlement = customerInfo.entitlements.active['Lucky Friday Pro'];

    //       const hasCancelled = entitlement && entitlement.willRenew === false;

    //       if (hasCancelled) {
    //         await store.cancelSubscription();
    //         await store.getUserAccountData();
    //       }
    //     } catch (e) {
    //       alert('presentCustomerCenter error:' + e.message);
    //       console.error('presentCustomerCenter error: ', e);
    //     }
    //   } else {
    //     loadingText.value = 'Canceling premium subscription to Lucky Friday ';
    //     loading.value = true;
    //     store.cancelSubscription().then((response) => {
    //       if (response.status === 200) {
    //         store.getUserAccountData().then(() => {
    //           loading.value = false;
    //         });
    //       }
    //     });
    //   }
    // };

    // const cancelSubscription = async () => {
    //   if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    //     try {
    //       await RevenueCatUI.presentCustomerCenter();
    //     } catch (e) {
    //       alert('presentCustomerCenter error: ' + e.message);
    //       console.error('presentCustomerCenter error:', e);
    //     }
    //   } else {
    //     // Stripe cancellation logic
    //     loadingText.value = 'Canceling premium subscription to Lucky Friday';
    //     loading.value = true;
    //     store.cancelSubscription().then((response) => {
    //       if (response.status === 200) {
    //         store.getUserAccountData().then(() => {
    //           loading.value = false;
    //         });
    //       }
    //     });
    //   }
    // };

    // const cancelSubscription = async () => {
    //   if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    //     await Browser.open({
    //       url: 'https://apps.apple.com/account/subscriptions',
    //     });
    //   } else {
    //     loadingText.value = 'Canceling premium subscription to Lucky Friday';
    //     loading.value = true;
    //     store.cancelSubscription().then((response) => {
    //       if (response.status === 200) {
    //         store.getUserAccountData().then(() => {
    //           loading.value = false;
    //         });
    //       }
    //     });
    //   }
    // };

    watch(
      () => store.user,
      (newUser) => {
        user.value = newUser;
        firstName.value = newUser.firstName;
        middleName.value = newUser.middleName;
        lastName.value = newUser.lastName;
        preferredName.value = newUser.preferredName;
        dateOfBirth.value = newUser.dateOfBirth;
        gender.value =
          newUser.gender === 'Non-Binary' ? 'Non-binary' : newUser.gender;
        occupationInput.value = newUser.occupation;
        email.value = newUser.email;
        address.value = newUser.address;
        city.value = newUser.city;
        state.value = newUser.state;
        zipCode.value = newUser.zipCode;
        phoneNumber.value = newUser.phoneNumber;
        dateOfBirthForEdit.value = newUser.dateOfBirth.split('T')[0];
      }
    );

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      await store.getUserAccountData().then(() => {
        loading.value = false;
      });
      user.value = store.user;
      user.value.dateOfBirth = formatDateOfBirth(user.value.dateOfBirth);

      if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
        isIosApp.value = true;
      }
    });

    const closeModal = () => {
      modalRef.value?.closeModal();
    };
    const closeDeleteAccountModal = () => {
      deleteModalRef.value?.closeModal();
    };
    const openCancelSubscriptionModal = () => {
      modalRef.value?.openModal();
    };
    const openDeleteAccountModal = () => {
      deleteModalRef.value?.openModal();
    };
    const cancelSubscriptionConfirmation = () => {
      cancelSubscription();
      modalRef.value?.closeModal();
    };

    const subscribePremium = async () => {
      if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
        try {
          const { result } = await RevenueCatUI.presentPaywall();
          if (
            result === PAYWALL_RESULT.PURCHASED ||
            result === PAYWALL_RESULT.RESTORED
          ) {
            loadingText.value = 'Activating Premium...';
            await store.activateIapSubscription();
            await store.getUserAccountData();
            loading.value = false;
          }
        } catch (e) {
          loading.value = false;
          alert('subscribePremium error: ' + e.message);
          console.error('Paywall error:', e);
        }
      } else {
        router.push({
          name: 'StripePayments',
          query: { isExistingUser: true },
        });
      }
    };

    // const subscribePremium = async () => {
    //   if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    //     try {
    //       const { result } = await RevenueCatUI.presentPaywall();
    //       if (
    //         result === PAYWALL_RESULT.PURCHASED ||
    //         result === PAYWALL_RESULT.RESTORED
    //       ) {
    //         await store.activateIapSubscription();
    //         await store.getUserAccountData();
    //       }
    //     } catch (e) {
    //       console.error('Paywall error:', e);
    //     }
    //   } else {
    //     router.push({
    //       name: 'StripePayments',
    //       query: { isExistingUser: true },
    //     });
    //   }
    // };

    return {
      store,
      router,
      editTransactionSliderSelected,
      UpdateUserProfile,
      user,
      firstName,
      middleName,
      lastName,
      preferredName,
      dateOfBirth,
      email,
      address,
      city,
      state,
      zipCode,
      phoneNumber,
      gender,
      formatDateOfBirth,
      dateOfBirthForEdit,
      validEmail,
      validZipcode,
      loading,
      loadingText,
      deleteAccount,
      premiumSubscription,
      subscribePremium,
      cancelSubscription,
      formatPhoneNumber,
      validPhoneNumber,
      occupations,
      occupationInput,
      filteredOccupations,
      filterOccupations,
      selectOccupation,
      handleOccupationBlur,
      modalRef,
      closeModal,
      cancelSubscriptionConfirmation,
      openCancelSubscriptionModal,
      closeDeleteAccountModal,
      openDeleteAccountModal,
      deleteModalRef,
      isIosApp,
      cancelError,
      cancelErrorModalRef,
    };
  },
};
</script>

<style scoped>
.up-view {
  max-width: 480px;
  margin: 0 auto;
}

.et-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1.5rem;
  background: #f0f0f4;
  border-radius: var(--radius-pill);
  padding: 4px;
}
.dark-theme .et-tabs {
  background: #1a2535;
}

.et-tab {
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.6rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #888;
  transition: all 0.2s ease;
}
.et-tab.active {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}

.up-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0 1.5rem;
  animation: slideUp 0.4s ease both;
}
.up-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  box-shadow: var(--shadow-purple);
  animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
}
.up-avatar-icon {
  color: white;
  font-size: 30px;
}
.up-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 2px;
  letter-spacing: -0.02em;
}
.dark-theme .up-name {
  color: white;
}
.up-preferred {
  font-size: 0.8rem;
  color: #888;
  margin: 0;
}

.up-info-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}

.et-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .et-info-row {
  border-bottom-color: #2a3a4e;
}
.et-info-row:last-child {
  border-bottom: none;
}
.et-info-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
}
.et-info-value {
  font-size: 0.82rem;
  font-weight: 500;
  color: #213547;
  text-align: right;
  max-width: 60%;
}
.dark-theme .et-info-value {
  color: #d4d2d2;
}

.autocomplete-list {
  padding: 0.25rem 0;
  margin-top: 4px;
}
.autocomplete-item {
  padding: 0.6rem 1rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #213547;
  border-bottom: 1px solid #f0f0f0;
  transition: background var(--transition-fast);
}
.autocomplete-item:last-child {
  border-bottom: none;
}
.autocomplete-item:hover {
  background: var(--purple-50);
  color: var(--color-accent-purple);
}
.dark-theme .autocomplete-item {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.dark-theme .autocomplete-item:hover {
  background: #1e1a40;
}

.delete-btn {
  border: 2px solid #d85a30;
  background: white;
  color: #d85a30;
  border-radius: var(--radius-pill);
  padding: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
}
.delete-btn:hover {
  background: #faece7;
  transform: translateY(-1px);
}
.delete-btn:active {
  transform: scale(0.97);
}
.dark-theme .delete-btn {
  background: transparent;
  color: #f09595;
  border-color: #f09595;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'User Profile'">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'UserAccount' })"
        />
      </template>
    </MainHeader>
    <div class="edit-account-view">
      <div class="view edit-account">
        <div>
          <Slider
            :options="sliderOptions"
            @sliderSelection="sliderOptionUpdated($event)"
          ></Slider>
        </div>
        <div v-if="!editTransactionSliderSelected">
          <div class="text-center pt-4 pb-4">
            <font-awesome-icon
              :icon="['fas', 'circle-user']"
              class="info-icon view-icon"
            />
          </div>
          <div class="user-info-card">
            <div>
              <label class="small-grey-text">Name</label>
              <p>
                {{ user.firstName }} {{ user.middleName }} {{ user.lastName }}
              </p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Preferred Display Name</label>
              <p>{{ user.preferredName }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Date of Birth</label>
              <p>{{ user.dateOfBirth ? user.dateOfBirth : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Gender</label>
              <p>{{ user.gender ? user.gender : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Occupation</label>
              <p>{{ user.occupation ? user.occupation : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Email Address</label>
              <p>{{ user.email }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Phone Number</label>
              <p>{{ user.phoneNumber ? user.phoneNumber : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Address</label>
              <p>{{ user.address ? user.address : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">City</label>
              <p>{{ user.city ? user.city : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">State</label>
              <p>{{ user.state ? user.state : 'N/A' }}</p>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Zipcode</label>
              <p>{{ user.zipCode ? user.zipCode : 'N/A' }}</p>
            </div>
          </div>
          <div v-if="store.isUserOver18">
            <div v-if="!user.premiumSubscription" class="mt-1">
              <button
                class="blue-button mt-4 w-100"
                @click="subscribePremium"
              >
                Subscribe to Premium
              </button>

            </div>

            <div v-else class="mt-1">
              <button
                class="blue-button w-100"
                @click="openCancelSubscriptionModal"
              >
                Cancel Premium Subscription
              </button>
            </div>
          </div>
          <div class="mt-1">
            <button class="red-button w-100" @click="openDeleteAccountModal">
              Delete Account
            </button>
          </div>
        </div>

        <div v-else>
          <div class="text-center pt-4 pb-4">
            <font-awesome-icon
              :icon="['fas', 'file-pen']"
              class="view-icon edit-icon"
            />
          </div>
          <form @submit.prevent="UpdateUserProfile">
            <fieldset>
              <div>
                <label class="small-grey-text" for="firstname"
                  >First Name *</label
                >
                <input
                  type="text"
                  class="input"
                  v-model="firstName"
                  required
                  id="firstname"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="displayname"
                  >Preferred Display Name</label
                >
                <input
                  type="text"
                  class="input"
                  v-model="preferredName"
                  id="displayname"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="middlename"
                  >Middle Name</label
                >
                <input
                  type="text"
                  class="input"
                  v-model="middleName"
                  id="middlename"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="lastname"
                  >Last Name *</label
                >
                <input
                  type="text"
                  class="input"
                  v-model="lastName"
                  required
                  id="lastname"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="dob">Date of Birth *</label>
                <input
                  id="dob"
                  type="date"
                  class="input block date-picker"
                  v-model="dateOfBirthForEdit"
                  required
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="gender">Gender</label>
                <select
                  id="gender"
                  v-model="gender"
                  class="form-select gender-select"
                >
                  <option value="" disabled>Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="job">Occupation</label>
                <input
                  id="job"
                  type="text"
                  class="input"
                  v-model="occupationInput"
                  placeholder="Start typing your occupation..."
                  @input="filterOccupations"
                  @blur="handleOccupationBlur"
                />
                <div
                  v-if="filteredOccupations.length > 0"
                  class="autocomplete-list wrapper-card"
                >
                  <div
                    v-for="(item, index) in filteredOccupations"
                    :key="index"
                    class="autocomplete-item"
                    @click="selectOccupation(item)"
                  >
                    {{ item }}
                  </div>
                </div>
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="email"
                  >Email Address *</label
                >
                <input
                  id="email"
                  type="emailInput"
                  class="input"
                  v-model="email"
                  autocomplete="email"
                  required
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  class="input"
                  v-model="phoneNumber"
                  autocomplete="tel"
                  pattern="(\([0-9]{3}\)|[0-9]{3})-[0-9]{3}-[0-9]{4}"
                  title="Enter a valid US phone number (e.g., 555-555-5555)"
                  placeholder="Enter a valid US phone number (e.g., 555-555-5555)"
                  @input="formatPhoneNumber"
                  maxlength="12"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="address">Address</label>
                <input
                  type="text"
                  class="input"
                  v-model="address"
                  id="address"
                  autocomplete="street-address"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="city">City</label>
                <input
                  type="text"
                  class="input"
                  v-model="city"
                  id="city"
                  autocomplete="address-level2"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="state">State</label>
                <input
                  type="text"
                  class="input"
                  v-model="state"
                  id="state"
                  autocomplete="address-level1"
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text" for="zipcode">Zipcode</label>
                <input
                  id="zipcode"
                  type="text"
                  class="input"
                  autocomplete="postal-code"
                  v-model="zipCode"
                />
              </div>
              <button
                role="button"
                type="submit"
                aria-label="submit update user profile"
                class="blue-button mt-4 w-100"
              >
                Submit
              </button>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
  <Modal ref="modalRef">
    <template #header>
      <h2>Confirm Premium Subscription Cancellation</h2>
    </template>
    <template #body>
      <p>
        You are cancelling your premium subscription. You will lose access to
        all of your premium linked bank accounts and their associated
        transactions.
      </p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button @click="closeModal" class="button teal-outline-button">
          Keep My Premium Subscription
        </button>
        <button @click="cancelSubscriptionConfirmation" class="teal-button">
          Cancel My Premium Subscription
        </button>
      </div>
    </template>
  </Modal>
  <Modal ref="deleteModalRef">
    <template #header>
      <h2>Delete Account</h2>
    </template>
    <template #body>
      <p>
        By deleting your account you are confirming that your budgets and
        account information will no longer be accessible. If you have a premium
        subscription it will be canceled automatically.
      </p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button
          @click="closeDeleteAccountModal"
          class="button teal-outline-button"
        >
          Keep My Account
        </button>
        <button @click="deleteAccount" class="teal-button">
          Close My Account
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { useMyStore } from '../../store';
import { useRouter } from 'vue-router';
import { onMounted, ref, watch } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { Occupation_List } from '../../utils/occupationList';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import MainHeader from '../../components/View-Header.vue';
import Slider from '../../components/Slider.vue';
import { Edit_Transaction_Types } from '../../utils/constants.js';
import { validEmail } from '../../utils/formValidation/validEmail.js';
import { validZipcode } from '../../utils/formValidation/validZipcode.js';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import Modal from '../../components/Main-Modal.vue';
import { RevenueCatUI, PAYWALL_RESULT } from '@revenuecat/purchases-capacitor-ui';

export default {
  components: {
    MainHeader,
    Slider,
    SkeletonLoader,
    Modal,
  },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const { logout } = useAuth0();
    const sliderOptions = [
      { label: 'Info', disabled: false },
      { label: 'Edit', disabled: true },
    ];
    let editTransactionSliderSelected = ref(false);
    const user = ref(store.user);
    const firstName = ref(user.value.firstName);
    const middleName = ref(user.value.middleName);
    const lastName = ref(user.value.lastName);
    const preferredName = ref(user.value.preferredName);
    const dateOfBirth = ref(user.value.dateOfBirth);
    const gender = ref(user.value.gender);
    const email = ref(user.value.email);
    const address = ref(user.value.address);
    const city = ref(user.value.city);
    const state = ref(user.value.state);
    const zipCode = ref(user.value.zipCode);
    const phoneNumber = ref(user.value.phoneNumber);
    const dateOfBirthForEdit = ref(user.value.dateOfBirth);
    const premiumSubscription = ref(user.value.premiumSubscription);
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const occupations = Occupation_List;
    const occupationInput = ref(
      user.value.occupation ? user.value.occupation : ''
    );
    const filteredOccupations = ref([]);
    const modalRef = ref(null);
    const deleteModalRef = ref(null);
    const isIosApp = ref(false);

    const filterOccupations = () => {
      if (!occupationInput.value) {
        filteredOccupations.value = [];
        return;
      }
      filteredOccupations.value = occupations
        .filter((occ) =>
          occ.toLowerCase().includes(occupationInput.value.toLowerCase())
        )
        .slice(0, 10);
    };

    const selectOccupation = (item) => {
      occupationInput.value = item;
      filteredOccupations.value = [];
    };

    const handleOccupationBlur = () => {
      setTimeout(() => {
        filteredOccupations.value = [];
      }, 200);
    };

    const formatPhoneNumber = (event) => {
      let digits = event.target.value.replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) {
        phoneNumber.value = digits;
      } else if (digits.length <= 6) {
        phoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        phoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    };

    const validPhoneNumber = (number) => {
      return number.length === 10;
    };

    const sliderOptionUpdated = (selection) => {
      if (selection === Edit_Transaction_Types.edit) {
        editTransactionSliderSelected.value = true;
      } else {
        editTransactionSliderSelected.value = false;
      }
    };

    const UpdateUserProfile = () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;

      try {
        let userData = {
          FirstName: firstName.value,
          MiddleName: middleName.value,
          LastName: lastName.value,
          PreferredName: preferredName.value
            ? preferredName.value
            : firstName.value,
          DateOfBirth: dateOfBirthForEdit.value
            ? new Date(dateOfBirthForEdit.value + 'T00:00:00').toISOString()
            : null,
          Gender: gender.value,
          Occupation: occupationInput.value,
          Email: email.value,
          Address: address.value,
          City: city.value,
          State: state.value,
          ZipCode: zipCode.value,
          PhoneNumber: phoneNumber.value,
          DateCreated: user.value.DateCreated,
        };
        store.saveUserAccount(userData).then((response) => {
          if (response.status === 200) {
            store.getUserAccountData().then(() => {
              user.value = store.user;
              user.value.dateOfBirth = formatDateOfBirth(
                user.value.dateOfBirth
              );
              editTransactionSliderSelected.value = false;
              loading.value = false;
            });
          }
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const formatDateOfBirth = (dateOfBirth) => {
      const date = new Date(dateOfBirth);
      const year = date.getUTCFullYear();
      const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
      const day = ('0' + date.getUTCDate()).slice(-2);
      return `${month}/${day}/${year}`;
    };

    const deleteAccount = () => {
      loadingText.value = 'Deleting Account...';
      loading.value = true;
      store.deleteUserAccount().then((response) => {
        if (response.status === 200) {
          loading.value = false;
          logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
        }
      });
      closeDeleteAccountModal();
    };

    const cancelSubscription = async () => {
      if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
        await Browser.open({ 
          url: 'https://apps.apple.com/account/subscriptions' 
        });
      } else {
        loadingText.value = 'Canceling premium subscription to Lucky Friday';
        loading.value = true;
        store.cancelSubscription().then((response) => {
          if (response.status === 200) {
            store.getUserAccountData().then(() => {
              loading.value = false;
            });
          }
        });
      }
};

    watch(
      () => store.user,
      (newUser) => {
        user.value = newUser;
        firstName.value = newUser.firstName;
        middleName.value = newUser.middleName;
        lastName.value = newUser.lastName;
        preferredName.value = newUser.preferredName;
        dateOfBirth.value = newUser.dateOfBirth;
        gender.value =
          newUser.gender === 'Non-Binary' ? 'Non-binary' : newUser.gender;
        occupationInput.value = newUser.occupation;
        email.value = newUser.email;
        address.value = newUser.address;
        city.value = newUser.city;
        state.value = newUser.state;
        zipCode.value = newUser.zipCode;
        phoneNumber.value = newUser.phoneNumber;
        dateOfBirthForEdit.value = newUser.dateOfBirth.split('T')[0];
      }
    );

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      await store.getUserAccountData().then(() => {
        loading.value = false;
      });
      user.value = store.user;
      user.value.dateOfBirth = formatDateOfBirth(user.value.dateOfBirth);

      if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
        isIosApp.value = true;
      }
    });

    const closeModal = () => {
      modalRef.value?.closeModal();
    };

    const closeDeleteAccountModal = () => {
      deleteModalRef.value?.closeModal();
    };

    const openCancelSubscriptionModal = () => {
      modalRef.value?.openModal();
    };

    const openDeleteAccountModal = () => {
      deleteModalRef.value?.openModal();
    };

    const cancelSubscriptionConfirmation = () => {
      cancelSubscription();
      modalRef.value?.closeModal();
    };

    const subscribePremium = async () => {
      if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
        try {
          const { result } = await RevenueCatUI.presentPaywall();
          if (result === PAYWALL_RESULT.PURCHASED || result === PAYWALL_RESULT.RESTORED) {
            await store.activateIapSubscription();
            await store.getUserAccountData();
          }
        } catch (e) {
          console.error('Paywall error:', e);
        }
      } else {
        router.push({
          name: 'StripePayments',
          query: { isExistingUser: true },
        });
      }
    };

    return {
      store,
      router,
      sliderOptions,
      sliderOptionUpdated,
      editTransactionSliderSelected,
      UpdateUserProfile,
      user,
      firstName,
      middleName,
      lastName,
      preferredName,
      dateOfBirth,
      email,
      address,
      city,
      state,
      zipCode,
      phoneNumber,
      gender,
      formatDateOfBirth,
      dateOfBirthForEdit,
      validEmail,
      validZipcode,
      loading,
      loadingText,
      deleteAccount,
      premiumSubscription,
      subscribePremium,
      cancelSubscription,
      formatPhoneNumber,
      validPhoneNumber,
      occupations,
      occupationInput,
      filteredOccupations,
      filterOccupations,
      selectOccupation,
      handleOccupationBlur,
      modalRef,
      closeModal,
      cancelSubscriptionConfirmation,
      openCancelSubscriptionModal,
      closeDeleteAccountModal,
      openDeleteAccountModal,
      deleteModalRef,
      isIosApp,
      subscribePremium
    };
  },
};
</script>

<style scoped>
.info-icon {
  color: #e8f44a;
}

.user-info-card {
  padding: 1em;
  border-radius: 5px;
  box-shadow: 0px 20px 20px rgba(0, 0, 0, 0.1);
}

.user-info-card p {
  margin: 0;
}

.light-theme .user-info-card {
  background: #f8f8f8;
}

.dark-theme .user-info-card {
  background: #212e3e;
  color: white;
}

.edit-account .input {
  width: 100%;
  box-sizing: border-box;
  font-size: 1em;
}

.gender-select {
  box-sizing: border-box;
  font-size: 1em;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 10px;
}

.autocomplete-list {
  padding-left: 1em;
}

.autocomplete-item {
  padding: 10px;
  cursor: pointer;
}

.autocomplete-item:hover {
  background: #f0f0f0;
}
</style> -->
